import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  deleteUnitRequest,
  getUnitWithDetailsRequest,
  updateUnitRequest,
  getUnitOptionsRequest,
} from '@/api';
import Button from '@/components/shared/Button';
import Card from '@/components/shared/Card';
import PropertyOverview from '@/components/dashboard/Property/PropertyOverview';
import UnitInformation from '@/components/dashboard/UnitEdit/UnitInformation';
import { UnitInformationData } from '@/components/dashboard/UnitEdit/UnitInformation/UnitInformation';
import UnitInclusions from '@/components/dashboard/UnitEdit/UnitInclusions';
import { UnitInclusionsData } from '@/components/dashboard/UnitEdit/UnitInclusions/UnitInclusions';
import UnitTenant from '@/components/dashboard/UnitEdit/UnitTenant';
import UnitLeaseTerms from '@/components/dashboard/UnitEdit/UnitLeaseTerms';
import { UnitLeaseTermsData } from '@/components/dashboard/UnitEdit/UnitLeaseTerms/UnitLeaseTerms';
import UnitRent from '@/components/dashboard/UnitEdit/UnitRent';
import { UnitRentData } from '@/components/dashboard/UnitEdit/UnitRent/UnitRent';
import {
  ILease,
  IUnit,
  PaymentMethod,
  PaymentPeriod,
  DetailedUnitResponse,
  UserRole,
} from '@/types';
import { SelectOption } from '@/types/api-types'; // Only import SelectOption
import getAxiosErrors from '@/utils/getAxiosErrors';
import { useCollapsibleSections } from '@/hooks/useCollapsibleSections';
import { useToast } from '@/components/shared/ToastProvider';
import './EditUnitPage.scss';

const baseClass = 'EditUnitPage';

const emptyLease: ILease = {
  isFixed: 'fixed',
  period: null,
  startDate: null,
  endDate: null,
  indeterminateStartDate: null,
};

const emptyUnit: IUnit = {
  number: '',
  type: 'residential', // Default type for empty unit
  floor: null,
  rooms: [],

  dimension: null,
  bedrooms: null,
  numberOfFloors: null,
  bathrooms: null,
  hasOtherInformation: false,
  otherInformation: '',
  characteristics: {
    parking: {
      hasIndoor: false,
      hasOutdoor: false,
      indoorCount: 0,
      outdoorCount: 0,
      indoorSpaces: '',
      outdoorSpaces: '',
    },
    storage: {
      hasLockerOrStorage: false,
      storageDescription: '',
    },
    otherAccessories: {
      hasOtherAccessories: false,
      otherAccessoriesDescription: '',
    },
    furniture: {
      tables: { count: 0, included: false },
      chairs: { count: 0, included: false },
      armchairs: { count: 0, included: false },
      chests: { count: 0, included: false },
      couches: { count: 0, included: false },
      beds: { count: 0, included: false },
      appliances: {
        dryer: false,
        stove: false,
        washer: false,
        microwave: false,
        dishwasher: false,
        refrigerator: false,
      },
      isIncluded: null,
      hasOther: false,
      otherDescription: '',
    },
  },
  invite: null,
  tenant: null,
  documents: [],
  lease: emptyLease,
  rentPrice: null,
  rentPricePeriod: PaymentPeriod.MONTH,
  costOfServices: null,
  costOfServicesPeriod: PaymentPeriod.MONTH,
  totalRentPrice: null,
  totalRentPricePeriod: PaymentPeriod.MONTH,
  rentPriceDescription: '',
  firstPaymentDate: null,
  paymentFirstDay: PaymentPeriod.MONTH,
  paymentMethod: PaymentMethod.CASH,
  paymentMethodOtherSpecify: '',
};

function EditUnitPage() {
  const { propertyId, unitId } = useParams<{
    propertyId: string;
    unitId: string;
  }>();
  const navigate = useNavigate();
  const [data, setData] = useState<IUnit | null>(null);
  // Explicitly define the type for options state
  const [options, setOptions] = useState<{
    roomTypes: SelectOption<string>[];
    unitTypes: SelectOption<string>[];
    floors: SelectOption<number>[];
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const numericId = Number(propertyId);
  const numericUnitId = Number(unitId);
  const isNumeric = !isNaN(numericId) && !isNaN(numericUnitId);
  const { showSuccess, showError } = useToast();

  const { getSectionState, toggleSection } = useCollapsibleSections(
    'edit-unit-page',
    {
      overview: false,
      information: false,
      inclusions: false,
      tenant: false,
      leaseTerms: false,
      rent: false,
    },
  );

  const unit = data;
  const property = data?.property;

  const mergeUnitData = (apiResponse: DetailedUnitResponse): IUnit => {
    // API returns flat structure
    const propertyFromApi = apiResponse.property;

    const mergedUnit: IUnit = {
      ...emptyUnit,
      id: apiResponse.id,
      number: apiResponse.number,
      type: (apiResponse.type as IUnit['type']) || null, // Ensure type is compatible with IUnit, allow null
      floor:
        apiResponse.floor !== null && apiResponse.floor !== undefined
          ? Number(apiResponse.floor) // Changed from String to Number
          : null,
      roomsCount: apiResponse.roomsCount || null, // Кількість кімнат
      rooms: apiResponse.rooms || [], // Масив кімнат (floor вже number!)
      dimension: apiResponse.dimension || null,
      bedrooms: apiResponse.bedrooms || null,
      numberOfFloors: apiResponse.numberOfFloors || null,
      bathrooms: apiResponse.bathrooms || null,
      hasOtherInformation: apiResponse.hasOtherInformation || false,
      otherInformation: apiResponse.otherInformation || '',

      characteristics: {
        parking: {
          ...emptyUnit.characteristics.parking,
          ...(apiResponse.characteristics?.parking || {}),
        },
        storage: {
          ...emptyUnit.characteristics.storage,
          ...(apiResponse.characteristics?.storage || {}),
        },
        otherAccessories: {
          ...emptyUnit.characteristics.otherAccessories,
          ...(apiResponse.characteristics?.otherAccessories || {}),
        },
        furniture: {
          ...emptyUnit.characteristics.furniture,
          ...(apiResponse.characteristics?.furniture || {}),
          appliances: {
            ...emptyUnit.characteristics.furniture.appliances,
            ...(apiResponse.characteristics?.furniture?.appliances || {}),
          },
          tables: {
            ...emptyUnit.characteristics.furniture.tables,
            ...(apiResponse.characteristics?.furniture?.tables || {}),
          },
          chairs: {
            ...emptyUnit.characteristics.furniture.chairs,
            ...(apiResponse.characteristics?.furniture?.chairs || {}),
          },
          armchairs: {
            ...emptyUnit.characteristics.furniture.armchairs,
            ...(apiResponse.characteristics?.furniture?.armchairs || {}),
          },
          chests: {
            ...emptyUnit.characteristics.furniture.chests,
            ...(apiResponse.characteristics?.furniture?.chests || {}),
          },
          couches: {
            ...emptyUnit.characteristics.furniture.couches,
            ...(apiResponse.characteristics?.furniture?.couches || {}),
          },
          beds: {
            ...emptyUnit.characteristics.furniture.beds,
            ...(apiResponse.characteristics?.furniture?.beds || {}),
          },
        },
      },
      lease: apiResponse.lease
        ? {
            ...emptyLease,
            isFixed:
              apiResponse.lease.status === 'active' ? 'fixed' : 'indeterminate',
            startDate: apiResponse.lease.startDate
              ? new Date(apiResponse.lease.startDate)
              : null,
            endDate: apiResponse.lease.endDate
              ? new Date(apiResponse.lease.endDate)
              : null,
            period: null,
            indeterminateStartDate: null,
          }
        : emptyLease,
      property: {
        id: propertyFromApi.id,
        name: propertyFromApi.name,
        type: propertyFromApi.type as 'residential' | 'commercial' | 'mixed',
        unitsCount: propertyFromApi.unitsCount || 0, // ← додано
        address: propertyFromApi.address
          ? {
              id: propertyFromApi.address.id,
              country: propertyFromApi.address.country,
              stateOrProvince: null,
              postalCode: propertyFromApi.address.postalCode,
              city: propertyFromApi.address.city,
              street: propertyFromApi.address.street,
              buildingNumber: null,
              apartmentNumber: null,
              municipality: null,
              latitude: propertyFromApi.address.latitude || null,
              longitude: propertyFromApi.address.longitude || null,
            }
          : {
              country: '',
              stateOrProvince: null,
              postalCode: null,
              city: null,
              street: null,
              buildingNumber: null,
              apartmentNumber: null,
              municipality: null,
              latitude: null,
              longitude: null,
            },
        owner: null,
      },
      tenant: apiResponse.tenant
        ? {
            id: apiResponse.tenant.id,
            firstname: apiResponse.tenant.name.split(' ')[0] || '',
            lastname:
              apiResponse.tenant.name.split(' ').slice(1).join(' ') || '',
            email: apiResponse.tenant.email,
            phone: '',
            avatar: apiResponse.tenant.avatar || null,
            addresses: null,
            role: UserRole.TENANT, // Використовуй enum!
            isActive: true,
            createdAt: new Date(),
            updatedAt: new Date(),
            deletedAt: new Date(),
            deleted: false,
          }
        : null,
    };

    return mergedUnit;
  };

  const handleApiError = useCallback(
    (err: any, context: string) => {
      const errorData = getAxiosErrors(err);
      let messages: string[] = [];

      if (errorData.errorMessage && Array.isArray(errorData.errorMessage)) {
        messages = errorData.errorMessage;
      } else {
        for (const key in errorData) {
          if (Array.isArray(errorData[key])) {
            messages.push(...errorData[key]);
          } else if (typeof errorData[key] === 'string') {
            messages.push(errorData[key]);
          }
        }
      }
      const errorMessage =
        messages.length > 0 ? messages.join(', ') : 'Unknown error occurred.';
      showError(`${context}: ${errorMessage}`);
      return errorMessage;
    },
    [showError],
  );

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setFetchError(null);
      try {
        const [unitRes, optionsRes] = await Promise.all([
          getUnitWithDetailsRequest(numericId, numericUnitId),
          getUnitOptionsRequest(),
        ]);

        const mergedUnit = mergeUnitData(unitRes.data);
        setData(mergedUnit);

        // Get unitTypes from filters (they have correct string values like "residential")
        const unitTypeFilter = optionsRes.data.filters?.find(
          (f: any) => f.key === 'type',
        );

        setOptions({
          roomTypes: optionsRes.data.selects.roomTypes,
          unitTypes:
            unitTypeFilter?.options?.filter((opt: any) => opt.value !== '') ||
            [],
          floors: optionsRes.data.selects.floors,
        });
      } catch (err) {
        handleApiError(err, 'Failed to load unit data');
        setFetchError('Failed to load unit data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    if (isNumeric) {
      fetchData();
    }
  }, [numericId, numericUnitId, isNumeric, handleApiError]);

  const updateUnit = async (payload: Partial<IUnit>) => {
    try {
      await updateUnitRequest(numericId, numericUnitId, payload);
      showSuccess('Unit updated successfully!');

      const unitRes = await getUnitWithDetailsRequest(numericId, numericUnitId);
      const mergedUnit = mergeUnitData(unitRes.data);
      setData(mergedUnit);
    } catch (err) {
      handleApiError(err, 'Failed to update unit');
      throw err;
    }
  };

  const handleSaveUnitInformation = async (
    sectionData: Partial<UnitInformationData>,
  ) => {
    const payload: Partial<IUnit> = {
      type: sectionData.type === '' ? null : sectionData.type, // Convert empty string to null
      floor:
        sectionData.floor !== null && sectionData.floor !== undefined
          ? Number(sectionData.floor) // Changed from String to Number
          : null,
      roomsCount: sectionData.roomsCount, // Кількість
      rooms: sectionData.rooms || [], // Масив (floor вже number!)
      dimension: sectionData.dimension,
      bedrooms: sectionData.bedrooms,
      numberOfFloors: sectionData.numberOfFloors,
      bathrooms: sectionData.bathrooms,
      hasOtherInformation: sectionData.hasOtherInformation,
      otherInformation: sectionData.otherInformation,
    };

    await updateUnit(payload);
  };

  const handleSaveUnitInclusions = async (
    sectionData: Partial<UnitInclusionsData>,
  ) => {
    await updateUnit(sectionData);
  };

  const handleSaveUnitLeaseTerms = async (
    sectionData: Partial<UnitLeaseTermsData>,
  ) => {
    await updateUnit(sectionData);
  };

  const handleSaveUnitRent = async (sectionData: Partial<UnitRentData>) => {
    await updateUnit(sectionData);
  };

  const onDelete = () => {
    setIsDeleting(true);
    deleteUnitRequest(numericId, numericUnitId)
      .then((res) => {
        if (res.statusText === 'OK') {
          showSuccess('Unit deleted successfully!');
          navigate(`/dashboard/properties/${numericId}`);
        } else {
          console.log('Error deleting unit');
          showError('Error deleting unit.');
        }
      })
      .catch((e) => {
        handleApiError(e, 'Error deleting unit');
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  if (isLoading) {
    return (
      <Card className={baseClass}>
        <h4>Loading...</h4>
      </Card>
    );
  }

  if (!isNumeric) {
    return (
      <Card className={baseClass}>
        <h4>Invalid property or unit ID</h4>
        <Button onClick={() => navigate('/dashboard/properties')}>
          Go back to properties
        </Button>
      </Card>
    );
  }

  if (fetchError) {
    return (
      <Card className={baseClass}>
        <h4>{fetchError}</h4>
        <Button onClick={() => navigate('/dashboard/properties')}>
          Go back to properties
        </Button>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card className={baseClass}>
        <h4>Unit data not found.</h4>
        <Button onClick={() => navigate('/dashboard/properties')}>
          Go back to properties
        </Button>
      </Card>
    );
  }

  return (
    <div className={baseClass}>
      <Card.Header className={`${baseClass}--header`}>
        <span className={`${baseClass}--header-text`}>
          <span className={`${baseClass}--header-text-prop`}>
            Unit: {numericUnitId}
          </span>
        </span>
        <div className={`${baseClass}--header-btns`}>
          <Button
            className={`${baseClass}--header-btn`}
            theme={'secondary'}
            onClick={() => {
              navigate(`/dashboard/properties/${numericId}`);
            }}
          >
            Back to property
          </Button>
          <Button
            className={`${baseClass}--overview-btn`}
            theme={'blue'}
            onClick={() => {
              navigate(
                `/dashboard/properties/${numericId}/units/${numericUnitId}`,
              );
            }}
          >
            <span className={`${baseClass}--overview-btn-content`}>
              <i className="fa-solid fa-circle-info"></i>
              Unit information
            </span>
          </Button>
        </div>
      </Card.Header>

      {property && (
        <PropertyOverview
          property={property}
          loading={isLoading}
          collapsed={getSectionState('overview')}
          onToggleCollapse={() => toggleSection('overview')}
          showMap={true}
        />
      )}

      <UnitInformation
        data={{
          type: unit?.type === '' ? null : unit?.type || null, // Convert empty string to null, default to null
          floor: unit?.floor ? Number(unit.floor) : null,
          roomsCount: unit?.roomsCount || null,
          rooms: unit?.rooms || [],
          dimension: unit?.dimension || null,
          bedrooms: unit?.bedrooms || null,
          numberOfFloors: unit?.numberOfFloors || null,
          bathrooms: unit?.bathrooms || null,
          hasOtherInformation: unit?.hasOtherInformation || false,
          otherInformation: unit?.otherInformation || '',
        }}
        options={options}
        onSave={handleSaveUnitInformation}
        collapsed={getSectionState('information')}
        onToggleCollapse={() => toggleSection('information')}
        className="mb-24"
      />

      <UnitInclusions
        data={{
          characteristics: unit?.characteristics || emptyUnit.characteristics,
        }}
        onSave={handleSaveUnitInclusions}
        collapsed={getSectionState('inclusions')}
        onToggleCollapse={() => toggleSection('inclusions')}
        className="mb-24"
      />

      <UnitTenant
        data={{
          tenant: unit?.tenant
            ? {
                id: unit.tenant.id,
                firstname: unit.tenant.firstname,
                lastname: unit.tenant.lastname,
                email: unit.tenant.email,
                phone: unit.tenant.phone,
                avatar: unit.tenant.avatar || undefined,
              }
            : null,
          invite: unit?.invite
            ? {
                id: unit.invite.id,
                token: unit.invite.token || '',
                email: unit.invite.email || undefined,
                createdAt: unit.invite.createdAt
                  ? new Date(unit.invite.createdAt)
                  : undefined,
              }
            : null,
        }}
        onCreateInvite={async (inviteData) => {
          // TODO: Implement create invite API call
          console.log('Create invite:', inviteData);
        }}
        onCancelInvite={async () => {
          // TODO: Implement cancel invite API call
          console.log('Cancel invite');
        }}
        onTerminateLease={async () => {
          // TODO: Implement terminate lease API call
          console.log('Terminate lease');
        }}
        collapsed={getSectionState('tenant')}
        onToggleCollapse={() => toggleSection('tenant')}
        className="mb-24"
      />

      <UnitLeaseTerms
        data={{
          lease: { ...emptyLease, ...unit?.lease } as ILease,
        }}
        onSave={handleSaveUnitLeaseTerms}
        collapsed={getSectionState('leaseTerms')}
        onToggleCollapse={() => toggleSection('leaseTerms')}
        className="mb-24"
      />

      <UnitRent
        data={{
          rentPrice: unit?.rentPrice || null,
          rentPricePeriod: unit?.rentPricePeriod || PaymentPeriod.MONTH,
          costOfServices: unit?.costOfServices || null,
          costOfServicesPeriod:
            unit?.costOfServicesPeriod || PaymentPeriod.MONTH,
          totalRentPrice: unit?.totalRentPrice || null,
          totalRentPricePeriod:
            unit?.totalRentPricePeriod || PaymentPeriod.MONTH,
          rentPriceDescription: unit?.rentPriceDescription || '',
          firstPaymentDate: unit?.firstPaymentDate || null,
          paymentFirstDay: unit?.paymentFirstDay || PaymentPeriod.MONTH,
          paymentMethod: unit?.paymentMethod || PaymentMethod.CASH,
          paymentMethodOtherSpecify: '',
        }}
        onSave={handleSaveUnitRent}
        collapsed={getSectionState('rent')}
        onToggleCollapse={() => toggleSection('rent')}
        className="mb-24"
      />

      <div className={'bottom-section-buttons'}>
        <Button
          theme={'danger'}
          isDisabled={isDeleting}
          isLoading={isDeleting}
          onClick={onDelete}
        >
          Delete unit
        </Button>
      </div>
    </div>
  );
}

export default EditUnitPage;
